import apiClient from './apiClient'
import type {
  LoginCredentials,
  LoginResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  User,
} from '@/types/models/User'
import { UserRole, UserStatus } from '@/types/enums'
// // import type { ApiResponse } from '@/types/api'

// Mock mode for development (when backend is not available)
const USE_MOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true' || false // Backend is ready - using real API

class AuthService {
  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    // Use mock authentication if backend is not available
    if (USE_MOCK_AUTH) {
      return this.mockLogin(credentials)
    }

    // Login endpoint returns data directly at root level (not wrapped in 'data' field)
    const response = await apiClient.post<LoginResponse>('/auth/login', credentials)
    // response is already the login data (access_token, user, etc.)
    return response as unknown as LoginResponse
  }

  /**
   * Mock login for development
   */
  private async mockLogin(credentials: LoginCredentials): Promise<LoginResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // Mock successful login with any credentials
    return {
      access_token: 'mock-jwt-token-' + Date.now(),
      token_type: 'Bearer',
      expires_in: 3600,
      user: {
        id: '1',
        tenant_id: 'tenant-1',
        name: credentials.email.split('@')[0] || 'User', // Use email prefix as name
        email: credentials.email,
        phone: null,
        role: UserRole.SUPER_ADMIN,
        status: UserStatus.ACTIVE,
        email_verified_at: new Date().toISOString(),
        two_factor_enabled: false,
        last_login_at: new Date().toISOString(),
        permissions: ['*'], // All permissions
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      },
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    if (USE_MOCK_AUTH) {
      // Mock logout - just return
      await new Promise(resolve => setTimeout(resolve, 200))
      return
    }

    await apiClient.post('/auth/logout')
  }

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    if (USE_MOCK_AUTH) {
      // Return mock user from localStorage
      const storedUser = localStorage.getItem('fuelflow_user')
      if (storedUser) {
        return JSON.parse(storedUser)
      }
      throw new Error('No user found')
    }

    // /auth/me returns { success, user } - user at root level
    const response = await apiClient.get<{ success: boolean; user: User }>('/auth/me')
    return (response as any).user
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/refresh')
    return response.data
  }

  /**
   * Request password reset
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    await apiClient.post('/auth/forgot-password', data)
  }

  /**
   * Reset password
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await apiClient.post('/auth/reset-password', data)
  }

  /**
   * Verify email
   */
  async verifyEmail(token: string): Promise<void> {
    await apiClient.post('/auth/verify-email', { token })
  }

  /**
   * Resend verification email
   */
  async resendVerification(): Promise<void> {
    await apiClient.post('/auth/resend-verification')
  }
}

export const authService = new AuthService()
export default authService
